import React, { useState, useEffect } from "react";
import { get } from "lodash";
import { fetchElectionComment, postComments, postElectionReaction } from "../../../api";
import styles from "./styles.m.css";
import Icon from "../../atoms/icon";
import { connect } from "react-redux";
import LoginMobile from "../header/user-login/login-mobile";
import { DeepBI } from "../../../integrations";
import useElectionUser from "../../rows/app-webview-userID/index";

const sign = require("jwt-encode");

const truncateContent = (content, maxLength) => {
  if (content.length <= maxLength) return content;
  return content.slice(0, maxLength) + "...";
};

const LoksabhaElectionCommentsWidget = props => {
  const [comments, setComments] = useState([]);
  const [username, setUsername] = useState("");
  const [description, setDescription] = useState("");
  const [formError, setFormError] = useState(false);
  const [offset, setOffset] = useState(0);
  const [totalComments, setTotalComments] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [reactionLoading, setReactionLoading] = useState({});
  const [IsOpened, setIsOpened] = useState(false);

  const COMMENTS_PER_PAGE = 30;
  const jwtKey = get(props, ["jwtKey"], "");
  const partyId = get(props, ["partyId"], "");
  const slug = get(props, ["title"], "");
  const areaEn = get(props, ["areaEn"], "");
  const thokkuthiSlug = get(props, ["thokkuthiSlug"], "");

  const { loggedOut, UserId, isAppView, isReady } = useElectionUser();

  function generateJWT(payload) {
    return sign(payload, jwtKey);
  }

  const fetchComments = (pageOffset = 0, userId = UserId) => {
    setIsLoading(true);
    const PartyData = { _tn: Date.now() };

    fetchElectionComment(generateJWT(PartyData), partyId, pageOffset, COMMENTS_PER_PAGE, userId)
      .then(res => {
        const newComments = get(res, ["data", "items", "data"], []);
        const total = get(res, ["data", "items", "total"], 0);

        if (pageOffset === 0) {
          setComments(newComments);
        } else {
          setComments(prevComments => [...prevComments, ...newComments]);
        }

        setTotalComments(total);
        setOffset(pageOffset + COMMENTS_PER_PAGE);
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  };

  // Runs when UserId is resolved from hook (handles both web and app)
  useEffect(
    () => {
      if (!isReady) return;
      fetchComments(0, UserId);
    },
    [isReady, UserId]
  );

  const handleSubmit = () => {
    const trimmedUsername = username.trim();
    const trimmedDescription = description.trim();

    if (trimmedUsername && trimmedDescription) {
      const formData = {
        username: trimmedUsername,
        problems: trimmedDescription,
        thokuthi_id: partyId,
        area_en: areaEn,
        area: slug,
        thokuthi_slug: thokkuthiSlug,
        id: UserId
      };

      postComments(generateJWT(formData))
        .then(() => {
          setUsername("");
          setDescription("");
          setShowSuccess(true);
        })
        .catch(error => {
          console.error("Error submitting form data:", error);
        });
    } else {
      console.error("Please fill in all fields before submitting the form.");
      setFormError(true);
    }
  };

  const loadMore = () => {
    if (!isLoading) {
      fetchComments(offset);
    }
  };

  const toggleCommentExpansion = commentIndex => {
    setComments(prevComments =>
      prevComments.map(
        (comment, index) => (index === commentIndex ? { ...comment, expanded: !comment.expanded } : comment)
      )
    );
  };

  const handleReaction = (commentId, reactionType, commentIndex) => {
    if (loggedOut) {
      setIsOpened(true);
      DeepBI.PingLoginevents("Login_widget_view", "", "", "", "", "election_like_Comment");
      return;
    }

    const reactionKey = `${commentId}_${reactionType}`;
    if (reactionLoading[reactionKey]) return;

    const comment = comments[commentIndex];
    const currentReaction = comment.liked ? "liked" : comment.disliked ? "disliked" : null;

    const isTogglingOff = currentReaction === reactionType;
    const apiStatus = isTogglingOff ? 0 : reactionType === "liked" ? 1 : 2;

    const previousComment = { ...comment };

    setComments(prevComments =>
      prevComments.map((c, i) => {
        if (i !== commentIndex) return c;
        let likesDelta = 0;
        let dislikesDelta = 0;

        if (isTogglingOff) {
          if (reactionType === "liked") likesDelta = -1;
          if (reactionType === "disliked") dislikesDelta = -1;
        } else if (reactionType === "liked") {
          likesDelta = 1;
          if (currentReaction === "disliked") dislikesDelta = -1;
        } else {
          dislikesDelta = 1;
          if (currentReaction === "liked") likesDelta = -1;
        }

        return {
          ...c,
          like_count: get(c, ["like_count"], 0) + likesDelta,
          dislike_count: get(c, ["dislike_count"], 0) + dislikesDelta,
          liked: isTogglingOff ? false : reactionType === "liked",
          disliked: isTogglingOff ? false : reactionType === "disliked"
        };
      })
    );

    setReactionLoading(prev => ({ ...prev, [reactionKey]: true }));

    const formData = { pblm_id: commentId, id: UserId, status: apiStatus };

    postElectionReaction(generateJWT(formData))
      .then(response => {
        if (response && response.data && response.data.success) {
          fetchComments(0);
        }
      })
      .catch(err => {
        console.error("Reaction post failed:", err);
        setComments(prevComments => prevComments.map((c, i) => (i !== commentIndex ? c : previousComment)));
      })
      .finally(() => {
        setReactionLoading(prev => {
          const newState = { ...prev };
          delete newState[reactionKey];
          return newState;
        });
      });
  };

  const handleMobileLoginClose = () => {
    setIsOpened(false);
  };

  // Show interactive elements only if:
  // - Normal web user (not isAppView) OR
  // - App user with valid UserId
  const showInteractions = !isAppView || !!UserId;

  return (
    <div className={`${styles["election-container"]} container`}>
      {IsOpened && <LoginMobile onClose={handleMobileLoginClose} />}

      {showInteractions && (
        <div className={styles["form-grid"]}>
          <div className={styles["form-txt"]}>
            <h1>
              <span>உங்கள் தொகுதியின் முக்கிய பிரச்னைகள் என்னென்ன?</span>
            </h1>
          </div>
          <div className={styles["form-content"]}>
            <p>
              {slug} தொகுதியின் முக்கிய பிரச்னைகளை இங்கே பதியுங்கள்; இவை அனைத்தும் வெற்றிபெறும் எம்.எல்.ஏ -க்களிடம்
              சேர்க்கப்படும்!
            </p>
            <div className={styles["comment-note"]}>
              குறிப்பு :- தற்போதைய எம்.எல்.ஏ.வை பற்றியோ அல்லது தனி நபர் குறித்த (பாராட்டியோ அல்லது / தரம் தாழ்ந்தோ)
              கருத்துக்களை பதிய வேண்டாம். பிரச்னைகளை மட்டும் பகிருங்கள்.
            </div>

            {/* Hide post comment section for app users with no userId */}
            {!showSuccess &&
              (loggedOut ? (
                <div className={styles["login-to-comment"]}>
                  <span className={styles["login-to-comment-label"]}>உங்கள் கருத்தை சமர்ப்பிக்க</span>
                  <button
                    className={styles["login-to-comment-cta"]}
                    onClick={() => {
                      setIsOpened(true);
                      DeepBI.PingLoginevents("Login_widget_view", "", "", "", "", "election_post_Comment");
                    }}
                  >
                    Login செய்யவும் <Icon type="double-arrow" />
                  </button>
                </div>
              ) : (
                <div className={styles["form"]}>
                  <div>
                    <input
                      type="text"
                      value={username}
                      onChange={e => setUsername(e.target.value)}
                      placeholder="பெயர்*"
                      className={formError && !username.trim() ? styles["error"] : ""}
                    />
                  </div>
                  <div />
                  <div className={styles["textarea-sec"]}>
                    <textarea
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      placeholder="தொகுதியின் முக்கிய பிரச்னைகள்*"
                      className={formError && !description.trim() ? styles["error"] : ""}
                    />
                  </div>
                  <div className={styles["submit-sec"]}>
                    <input type="button" value="உங்கள் கருத்தை சமர்ப்பிக்க" onClick={handleSubmit} />
                  </div>
                </div>
              ))}

            {showSuccess && (
              <div className={styles["success-content"]}>
                <Icon type="tick" />
                <p>
                  உங்கள் கருத்து வெற்றிகரமாகப் பதிவு செய்தமைக்கு நன்றி. ஜூனியர் விகடன் ஆசிரியர் குழுவின் பரிசீலனைக்குப்
                  பின்னர் அது உங்கள் தொகுதியின் சிறப்பு பக்கத்தில் பிரசுரமாகும். தொடர்ந்து விகடனுடன் இணைந்திருங்கள்!
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {comments &&
        comments.length > 0 && (
          <div className={styles["comments-wrapper"]}>
            <div className={styles["comments-wrapper-section"]}>
              <h1>தொகுதியின் நிலை - மக்கள் குரல்</h1>
              <div className={styles["comments-height"]}>
                {comments.map((comment, index) => {
                  const commentId = get(comment, ["pblm_id"], index);
                  const userReaction = comment.liked ? "liked" : comment.disliked ? "disliked" : null;
                  const likes = get(comment, ["like_count"], 0);
                  const dislikes = get(comment, ["dislike_count"], 0);

                  return (
                    <ul key={commentId} className={styles["comments-section"]}>
                      <li className={styles["comments-header"]}>
                        <Icon type="icon-user" />
                        <div>
                          <h2>{get(comment, ["user_name"], "")}</h2>
                        </div>
                      </li>

                      <li className={styles["comments-pblm"]}>
                        {comment.expanded ? (
                          <div>
                            {get(comment, ["thokuthi_pblm"], "")}
                            <button onClick={() => toggleCommentExpansion(index)} className={styles["read-more-less"]}>
                              Read Less
                            </button>
                          </div>
                        ) : (
                          <div>
                            {truncateContent(get(comment, ["thokuthi_pblm"], ""), 200)}
                            {get(comment, ["thokuthi_pblm"], "").length > 200 && (
                              <button
                                onClick={() => toggleCommentExpansion(index)}
                                className={styles["read-more-less"]}
                              >
                                Read More
                              </button>
                            )}
                          </div>
                        )}
                      </li>

                      {/* Hide like/dislike for app users with no userId */}
                      {showInteractions && (
                        <li className={styles["comments-reactions"]}>
                          <div
                            className={`${styles["reaction-btn"]} ${
                              userReaction === "liked" ? styles["reaction-active"] : ""
                            }`}
                            onClick={() => handleReaction(commentId, "liked", index)}
                            aria-label="Like"
                          >
                            <Icon type="like" />
                            <span className={styles["reaction-count"]}>{likes}</span>
                          </div>

                          <div
                            className={`${styles["reaction-btn"]} ${
                              userReaction === "disliked" ? styles["reaction-active"] : ""
                            }`}
                            onClick={() => handleReaction(commentId, "disliked", index)}
                            aria-label="Dislike"
                          >
                            <Icon type="unlike" />
                            <span className={styles["reaction-count"]}>{dislikes}</span>
                          </div>
                        </li>
                      )}
                    </ul>
                  );
                })}
              </div>

              {comments.length < totalComments && (
                <button
                  onClick={loadMore}
                  className={styles["load-more"]}
                  disabled={isLoading}
                  style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? "wait" : "pointer" }}
                >
                  {isLoading ? <div>Loading...</div> : "Load More"}
                </button>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

function mapStateToProps(state) {
  return { jwtKey: get(state, ["qt", "config", "JwtKey"]) };
}

export default connect(mapStateToProps)(LoksabhaElectionCommentsWidget);
